import { useState, useEffect } from 'react';
import { adminCompanyAPI } from '../../api/axios';
import { toast } from 'react-toastify';
import AdminNav from '../../components/AdminNav';
import { FileText, CheckCircle, XCircle, Loader2, Clock } from 'lucide-react';

const statusConfig = {
  PENDING:   { label: 'Pending',   color: 'bg-yellow-100 text-yellow-700' },
  APPROVED:  { label: 'Approved',  color: 'bg-green-100 text-green-700' },
  REJECTED:  { label: 'Rejected',  color: 'bg-red-100 text-red-700' },
  WITHDRAWN: { label: 'Withdrawn', color: 'bg-gray-100 text-gray-600' },
};

export default function CompanyHireRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('PENDING');
  const [actionLoading, setActionLoading] = useState({});
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectNote, setRejectNote] = useState('');

  useEffect(() => { loadRequests(); }, [filter]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const res = await adminCompanyAPI.getHireRequests(filter);
      setRequests(res.data.data?.content || []);
    } catch { toast.error('Failed to load hire requests'); }
    finally { setLoading(false); }
  };

  const handleApprove = async (id) => {
    setActionLoading((p) => ({ ...p, [id]: 'approve' }));
    try {
      await adminCompanyAPI.approveHireRequest(id);
      setRequests((p) => p.filter((r) => r.id !== id));
      toast.success('Approved! Company can now contact the staff.');
    } catch { toast.error('Failed to approve'); }
    finally { setActionLoading((p) => ({ ...p, [id]: null })); }
  };

  const handleRejectSubmit = async () => {
    setActionLoading((p) => ({ ...p, [rejectModal]: 'reject' }));
    try {
      await adminCompanyAPI.rejectHireRequest(rejectModal, rejectNote);
      setRequests((p) => p.filter((r) => r.id !== rejectModal));
      toast.success('Hire request rejected');
      setRejectModal(null); setRejectNote('');
    } catch { toast.error('Failed to reject'); }
    finally { setActionLoading((p) => ({ ...p, [rejectModal]: null })); }
  };

  const tabs = ['PENDING', 'APPROVED', 'REJECTED', 'WITHDRAWN'];

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav title="Company Hire Requests" />

      <div className="max-w-5xl mx-auto px-6 py-8">

        {/* Status tabs */}
        <div className="flex gap-1.5 mb-6 flex-wrap">
          {tabs.map((tab) => (
            <button key={tab} onClick={() => setFilter(tab)}
              className={`px-5 py-2 rounded-xl text-sm font-semibold transition
                ${filter === tab
                  ? 'bg-slate-900 text-white shadow'
                  : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {statusConfig[tab]?.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-24">
            <Clock className="h-14 w-14 text-gray-200 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-400">
              No {filter.toLowerCase()} hire requests
            </h3>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((req) => {
              const cfg = statusConfig[req.status] || statusConfig.PENDING;
              return (
                <div key={req.id}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center shrink-0">
                      <FileText className="h-5 w-5 text-teal-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-bold text-slate-800">
                          {req.staffFirstName} {req.staffLastName}
                        </h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.color}`}>
                          {cfg.label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400">Staff ID: #{req.staffId}</p>
                      {req.staffTypeName && (
                        <p className="text-sm text-gray-500 mt-0.5">{req.staffTypeName}</p>
                      )}
                      {req.message && (
                        <p className="text-sm text-gray-600 mt-1.5 italic bg-gray-50 rounded-lg px-3 py-2">
                          "{req.message}"
                        </p>
                      )}
                      {req.adminNote && (
                        <p className="text-xs text-red-500 mt-1">Note: {req.adminNote}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-2">
                        {new Date(req.createdAt).toLocaleDateString('en-IN', {
                          day: 'numeric', month: 'short', year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>

                  {req.status === 'PENDING' && (
                    <div className="flex flex-col gap-2 shrink-0">
                      <button onClick={() => handleApprove(req.id)}
                        disabled={!!actionLoading[req.id]}
                        className="flex items-center gap-1.5 bg-green-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-green-700 transition disabled:opacity-60">
                        {actionLoading[req.id] === 'approve'
                          ? <Loader2 className="h-4 w-4 animate-spin" />
                          : <CheckCircle className="h-4 w-4" />}
                        Approve
                      </button>
                      <button
                        onClick={() => { setRejectModal(req.id); setRejectNote(''); }}
                        disabled={!!actionLoading[req.id]}
                        className="flex items-center gap-1.5 bg-red-50 text-red-600 border border-red-200 px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-red-100 transition disabled:opacity-60">
                        <XCircle className="h-4 w-4" /> Reject
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="font-bold text-slate-800 mb-4">Reject Hire Request</h3>
            <textarea value={rejectNote} onChange={(e) => setRejectNote(e.target.value)}
              placeholder="Reason for rejection (optional)..."
              rows={3}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none" />
            <div className="flex gap-3 mt-4">
              <button onClick={handleRejectSubmit}
                disabled={actionLoading[rejectModal] === 'reject'}
                className="flex-1 bg-red-600 text-white py-2.5 rounded-xl font-bold text-sm hover:bg-red-700 disabled:opacity-60 flex items-center justify-center gap-2">
                {actionLoading[rejectModal] === 'reject'
                  ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Confirm Reject
              </button>
              <button onClick={() => setRejectModal(null)}
                className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-xl font-semibold text-sm hover:bg-gray-200">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}